/**
 * prefabAssetRefs — which of a prefab's (or gesture's) asset references name an
 * asset the server could not produce.
 *
 * The failure this exists to make visible: a prefab row carries
 * `bodyAnimationId`, the AAM cannot resolve that row, and it serves the prefab
 * with the body URL simply ABSENT — byte-for-byte identical on the wire to a
 * prefab that legitimately has no body clip. `resolvePrefabClipNames` then
 * returns `bodyClipName: null`, `_doPlayPrefab` has nothing to look up, and the
 * prefab plays its face and audio with no body motion. Silently, on every
 * trigger. Observed 2026-08-31 on staging myra's `highfive`, whose
 * `bodyAnimationId` (3e5648d5-…) exists on production and not on staging, so the
 * two environments disagreed and neither said anything.
 *
 * Two sources, deliberately, and the DERIVED one is why this module exists
 * rather than just reading a server field:
 *
 *   1. The server's own `danglingRefs` / `dangling_refs` (avatar-asset-manager
 *      app/services/assetlinks), when the deployment serving us has it.
 *   2. DERIVED from the row: an id field that is set while its URL field is not.
 *
 * The client cannot depend on (1). Staging and production run different AAM
 * builds routinely, and the whole point is to be loud on the environment where
 * the data is broken — which is exactly the environment most likely to be
 * running the older API. (2) needs nothing from the server and catches the same
 * rows, so it is the floor and (1) is the corroboration.
 *
 * Pure — no I/O, no window — so the rule is unit-testable on its own
 * (tests/unit/prefabAssetRefs.test.mjs).
 */

// id field → the URL fields that stand for it, camelCase and snake_case, because
// the AAM emits both spellings for every one of these and different call sites
// read different ones.
const REF_FIELDS = [
  { id: ['bodyAnimationId', 'body_animation_id'], urls: ['bodyFileUrl', 'body_glb_url'], label: 'body animation' },
  { id: ['faceAnimationId', 'face_animation_id'], urls: ['faceFileUrl', 'face_json_url'], label: 'face animation' },
  { id: ['audioAssetId', 'audio_asset_id'], urls: ['audioFileUrl', 'audio_url'], label: 'audio' },
]

const firstSet = (row, keys) => {
  for (const k of keys) {
    const v = row?.[k]
    if (v != null && v !== '') return v
  }
  return null
}

/**
 * The dangling asset references on one prefab/gesture row.
 * @param {object|null} row a prefab or gesture link row as the AAM serves it
 * @returns {Array<{field: string, label: string, id: string, source: 'server'|'derived'}>}
 *   empty for a healthy row (including one that simply links no assets)
 */
export function danglingAssetRefs(row) {
  if (!row) return []
  // Whatever the server told us, normalised to a Set of id-field names.
  const declared = new Set([...(row.danglingRefs ?? []), ...(row.dangling_refs ?? [])])
  const out = []
  for (const ref of REF_FIELDS) {
    const id = firstSet(row, ref.id)
    if (id == null) continue                    // nothing linked — not a defect
    const url = firstSet(row, ref.urls)
    const serverSaid = ref.id.some((k) => declared.has(k))
    if (url != null && !serverSaid) continue    // resolved fine
    out.push({ field: ref.id[0], label: ref.label, id: String(id), source: serverSaid ? 'server' : 'derived' })
  }
  return out
}

/**
 * A one-line human report for the console, or null when the row is healthy.
 * Names the row AND the unresolvable ids — the two things needed to fix the data.
 * @param {object|null} row
 * @param {string} [kind] 'prefab' | 'gesture', for the message
 * @returns {string|null}
 */
export function danglingRefsMessage(row, kind = 'prefab') {
  const bad = danglingAssetRefs(row)
  if (!bad.length) return null
  const what = bad.map((b) => `${b.label} (${b.field}=${b.id})`).join(', ')
  const name = row?.tag ?? row?.name ?? row?.id ?? '(unnamed)'
  return `${kind} "${name}" references ${bad.length === 1 ? 'an asset' : 'assets'} that `
    + `${bad.length === 1 ? 'does' : 'do'} not exist on this server: ${what}. `
    + `It will play without ${bad.map((b) => b.label).join(' / ')} — fix the reference in the content browser.`
}
